import type { ReservationCartProduct } from '@iheartjane/data-access';
import type { ComplianceLanguageSettings, Nullable } from '@iheartjane/types';
import { formatCurrency } from '@iheartjane/utils';
import compact from 'lodash-es/compact';
import intersection from 'lodash-es/intersection';
import maxBy from 'lodash-es/maxBy';
import minBy from 'lodash-es/minBy';

import {
  WEIGHT_LABELS,
  WEIGHT_TO_GRAMS,
} from '@jane/bloom/dataAccess/constants';
import type { ProductApplicableIds } from '@jane/bloom/types/schemas/marketplace/storeSpecials';
import { areAllDiscountsTheSamePercentage } from '@jane/bloom/utils/product/areAllDiscountsTheSamePercentage';
import {
  getDiscountWeightKey,
  getWeightKey,
} from '@jane/bloom/utils/product/getWeightKey';
import type { AutocompleteProduct } from '@jane/bloom/viewModels/autocompleteProduct';
import type { ProductViewModel } from '@jane/bloom/viewModels/productDetail';
import type { ProductSearchResultViewModel } from '@jane/bloom/viewModels/productSearch';
import type { AlgoliaProduct } from '@jane/search/types';

/**
 * @description Remove trailing zeros if price is a whole number
 * @param price - The price to standardize
 * @returns The price to display
 * @example '20.00' -> 20
 * @example '20.15' -> 20.15
 */
export const getStandardizedPricing = (price: string | number): number =>
  parseFloat(price.toString());

export const getPriceIncludingDiscounts = (
  product:
    | ProductSearchResultViewModel
    | ProductViewModel
    | AlgoliaProduct
    | AutocompleteProduct,
  weight: string
) => {
  const weightKey = getWeightKey(weight);
  const discountWeightKey = getDiscountWeightKey(weight);

  const price = product[weightKey];
  const discountedPrice = product[discountWeightKey];

  if (price || discountedPrice) {
    if (discountedPrice) {
      return getStandardizedPricing(discountedPrice);
    }

    if (price) {
      return getStandardizedPricing(price);
    }
  }

  return null;
};

export const getPriceBeforeDiscounts = (
  product: ProductSearchResultViewModel | ProductViewModel | AlgoliaProduct,
  weight: string
) => {
  const weightKey = getWeightKey(weight);

  const price = product[weightKey];

  if (price) return getStandardizedPricing(price);
  return 0;
};

export const getProductPriceBreakdown = (
  product: ProductSearchResultViewModel | ProductViewModel | AlgoliaProduct,
  filteredWeights?: string[]
) => {
  const hasAvailableWeights = product.available_weights.length > 0;
  const weights =
    filteredWeights && filteredWeights.length > 0
      ? filteredWeights
      : hasAvailableWeights
        ? product.available_weights
        : ['each'];
  return weights.map((weight) => ({
    discountedPrice: getPriceIncludingDiscounts(product, weight),
    price: getPriceBeforeDiscounts(product, weight),
    weight,
  }));
};

// Tie-breaks price comparisons by weight so that when multiple weights share the
// same price (common on bulk specials with a flat discount), the range still
// reflects the full weight span — e.g. "$10/3.5g - $10/7g" instead of collapsing
// to "$10/3.5g - $10/3.5g". Multiplier dominates any realistic weight in grams.
const PRICE_TIEBREAK_MULTIPLIER = 1_000_000;
const weightInGrams = (weight: string) => WEIGHT_TO_GRAMS[weight] ?? 0;

export const getProductPriceRange = (
  product: ProductSearchResultViewModel | ProductViewModel | AlgoliaProduct,
  weights?: string[]
) => {
  const priceProductBreakdown = getProductPriceBreakdown(product, weights);

  const highestPrice = maxBy(
    priceProductBreakdown,
    (entry) =>
      (entry.price ?? 0) * PRICE_TIEBREAK_MULTIPLIER +
      weightInGrams(entry.weight)
  );
  const highestDiscountedPrice = maxBy(
    priceProductBreakdown,
    (entry) =>
      (entry.discountedPrice ?? 0) * PRICE_TIEBREAK_MULTIPLIER +
      weightInGrams(entry.weight)
  );
  const lowestPrice = minBy(
    priceProductBreakdown,
    (entry) =>
      (entry.price ?? 0) * PRICE_TIEBREAK_MULTIPLIER +
      weightInGrams(entry.weight)
  );
  const lowestDiscountedPrice = minBy(
    priceProductBreakdown,
    (entry) =>
      (entry.discountedPrice ?? 0) * PRICE_TIEBREAK_MULTIPLIER +
      weightInGrams(entry.weight)
  );

  return {
    highestDiscountedPrice,
    highestPrice,
    lowestDiscountedPrice,
    lowestPrice,
  };
};

/**
 * @example "$22" to represent '$22 each'
 * @example "$22/3.5g"
 */
export const formattedWeightedPrice = (price: number | null, weight: string) =>
  compact([formatCurrency(price, true), WEIGHT_LABELS[weight]]).join('/');

export const getProductPriceLabels = (
  product: ProductSearchResultViewModel | ProductViewModel | AlgoliaProduct,
  applicablePriceIds?: ProductApplicableIds[] | null
) => {
  const weights = applicablePriceIds
    ? [...intersection(applicablePriceIds, product.available_weights)]
    : [...product.available_weights];
  const {
    highestDiscountedPrice,
    highestPrice,
    lowestDiscountedPrice,
    lowestPrice,
  } = getProductPriceRange(product, weights);
  const hasMultipleWeights = weights.length > 1;
  const productPriceLabels = {
    discountedPriceLabel: '',
    priceLabel: '',
  };

  if (highestPrice && lowestPrice) {
    productPriceLabels.priceLabel = hasMultipleWeights
      ? `${formattedWeightedPrice(lowestPrice.price, lowestPrice.weight)} - ${formattedWeightedPrice(highestPrice.price, highestPrice.weight)}`
      : formattedWeightedPrice(
          lowestPrice.price,
          product.amount ? '' : lowestPrice.weight
        );
  }

  if (highestDiscountedPrice && lowestDiscountedPrice) {
    productPriceLabels.discountedPriceLabel = hasMultipleWeights
      ? `${formattedWeightedPrice(lowestDiscountedPrice.discountedPrice, lowestDiscountedPrice.weight)} - ${formattedWeightedPrice(highestDiscountedPrice.discountedPrice, highestDiscountedPrice.weight)}`
      : formattedWeightedPrice(
          lowestDiscountedPrice.discountedPrice,
          product.amount ? '' : lowestDiscountedPrice.weight
        );
  }

  return productPriceLabels;
};

export const calculateDiscountPercentage = (
  originalPrice: number | undefined,
  discountedPrice?: number | null
) => {
  if (!discountedPrice || !originalPrice || discountedPrice === originalPrice) {
    return null;
  }
  return Math.round(((originalPrice - discountedPrice) / originalPrice) * 100);
};

export const hasSpecialApplied = (
  product:
    | ProductSearchResultViewModel
    | ProductViewModel
    | AlgoliaProduct
    | ReservationCartProduct
) => product.special_amount || product.special_id || product.special_title;

export const getMiniSpecialBadgeLabel = (
  product: ProductSearchResultViewModel | ProductViewModel | AlgoliaProduct
) => {
  if (!hasSpecialApplied(product)) {
    return '';
  }
  if (product.special_custom_badge && product.special_custom_badge.length < 5) {
    return product.special_custom_badge;
  }
  const shouldDisplayComplianceLanguage =
    product.special_amount?.toUpperCase() === 'SALE';

  if (shouldDisplayComplianceLanguage) {
    return 'Deal';
  }

  if (
    product.available_weights.length > 1 &&
    !areAllDiscountsTheSamePercentage(product)
  ) {
    return 'Deal';
  }
  const { lowestPrice } = getProductPriceRange(product);
  const discountPercent = calculateDiscountPercentage(
    lowestPrice?.price,
    lowestPrice?.discountedPrice
  );

  if (discountPercent) {
    return `-${discountPercent}%`;
  }
  return 'Deal';
};

export const getSpecialBadgeLabel = (
  product: ProductSearchResultViewModel | ProductViewModel | AlgoliaProduct,
  storeComplianceLanguageSettings?: Nullable<ComplianceLanguageSettings>
) => {
  if (!hasSpecialApplied(product)) {
    return '';
  }
  if (product.special_custom_badge) {
    return product.special_custom_badge;
  }
  const shouldDisplayComplianceLanguage =
    product.special_amount?.toUpperCase() === 'SALE';

  const complianceLanguage =
    storeComplianceLanguageSettings?.['specials_badge'] ||
    storeComplianceLanguageSettings?.['specials'];

  if (shouldDisplayComplianceLanguage) {
    return complianceLanguage || 'Deal';
  }

  return product.special_amount;
};

export const getCartSpecialBadgeLabel = (
  product: ReservationCartProduct,
  storeComplianceLanguageSettings?: Nullable<ComplianceLanguageSettings>
) => {
  if (!hasSpecialApplied(product)) {
    return '';
  }
  const shouldDisplayComplianceLanguage =
    product.special_amount?.toUpperCase() === 'SALE';

  const complianceLanguage =
    storeComplianceLanguageSettings?.['specials_badge'] ||
    storeComplianceLanguageSettings?.['specials'];

  if (shouldDisplayComplianceLanguage) {
    return complianceLanguage || 'Deal';
  }

  const discountPercent = calculateDiscountPercentage(
    getStandardizedPricing(product.checkout_price),
    getStandardizedPricing(product.discounted_checkout_price)
  );

  if (discountPercent) {
    return `-${discountPercent}%`;
  }
  return 'Deal';
};
